import { getAuthAxios } from "@artprov/design-system";
import * as sentry from "@sentry/browser";

/**
 * Helper para obtener la instancia de API (lazy loading)
 * Evita el error de registry no inicializado
 */
const getApi = () => getAuthAxios("compras-back");

export const getFacturas = async (params = {}, options = {}) => {
  try {
    const queryParams = {
      offset: params.offset || 0,
      limit: params.limit || 10,
      ...params,
    };
    
    const res = await getApi().get("/facturas", {
      params: queryParams,
      ...options,
    });
    
    let facturas = [];
    if (Array.isArray(res.data)) {
      facturas = res.data;
    } else if (res.data?.data?.facturas && Array.isArray(res.data.data.facturas)) {
      facturas = res.data.data.facturas;
    }
    
    const processedHeaders = {};
    if (res.headers) {
      Object.keys(res.headers).forEach(key => {
        const value = res.headers[key];
        if (typeof value === 'string' && !isNaN(value) && !isNaN(parseFloat(value))) {
          processedHeaders[key] = parseFloat(value);
        } else {
          processedHeaders[key] = value;
        }
      });
    }
    
    return { facturas, headers: processedHeaders };
  } catch (error) {
    sentry.captureException(error);
    throw error;
  }
};

export const getAcciones = async (centroCosto = null) => {
  try {
    const res = await getApi().get("/dropdown/acciones", {
      params: {
        centroCosto
      }
    });
    return res.data;
  } catch (error) {
    sentry.captureException(error);
    throw error;
  }
};

export const getComprobanteAfip = async () => {
  try {
    const res = await getApi().get("/dropdown/comprobanteafip");
    return res.data;
  } catch (error) {
    sentry.captureException(error);
    throw error;
  }
};

export const getEstadosFactura = async () => {
  try {
    const res = await getApi().get("/dropdown/estadosfactura");
    return res.data;
  } catch (error) {
    sentry.captureException(error);
    throw error;
  }
};

/**
 * Obtiene detalle de una factura
 * @param {number} id - ID de la factura
 * @param {number} centroCosto - Centro de costo del usuario (debe pasarse desde componente)
 * @param {string} username - Username del usuario (debe pasarse desde componente)
 */
export const getDetalleFactura = async (id, centroCosto = 0, username = null) => {
  try {
    const res = await getApi().get(`/factura`, {
      params: {
        id,
        centro_costo: centroCosto,
        username
      }
    });
    return res.data;
  } catch (error) {
    sentry.captureException(error);
    throw error;
  }
};

/**
 * Edita una factura
 * @param {Object} payload - Datos de la factura
 * @param {string} username - Username del usuario (debe pasarse desde componente)
 */
export const editarFactura = async (payload = {}) => {
  try {
    const body = {
      idcomp: payload.idcomp,
      username: payload.username,
      orden_compra: payload.orden_compra,
      num_despacho: payload.num_despacho,
    };    
    const res = await getApi().post("/factura", body);
    return res.data;
  } catch (error) {
    sentry.captureException(error);
    throw error;
  }
};

/**
 * Aprueba una factura
 * @param {number} id - ID de la factura
 * @param {string} comentario - Comentario opcional
 * @param {string} username - Username del usuario
 * @param {number} centroCosto - Centro de costo del usuario
 */
export const aprobarFactura = async (id, comentario = null, username = null, centroCosto = null) => {
  try {
    const body = {
      username,
      comentario,
      centro_costo: centroCosto,
    };

    const res = await getApi().post(`/factura/aprobar/${id}`, body);
    return res.data;
  } catch (error) {
    sentry.captureException(error);
    throw error;
  }
};

/**
 * Rechaza una factura
 * @param {number} id - ID de la factura
 * @param {string} comentario - Comentario opcional
 * @param {string} username - Username del usuario
 * @param {number} centroCosto - Centro de costo del usuario
 */
export const rechazarFactura = async (id, comentario = null, username = null, centroCosto = null) => {
  try {
    const body = {
      username,
      comentario,
      centro_costo: centroCosto,
    };

    const res = await getApi().post(`/factura/rechazar/${id}`, body);
    return res.data;
  } catch (error) {
    sentry.captureException(error);
    throw error;
  }
};

/**
 * Cambia el estado de una factura
 * @param {number} id - ID de la factura
 * @param {string} estado - Estado a establecer (ej: 'En progreso')
 * @param {string} comentario - Comentario opcional
 * @param {string} username - Username del usuario
 * @param {number} centroCosto - Centro de costo del usuario
 */
export const editarEstadoFactura = async (id, estado, comentario = null, username = null, centroCosto = null) => {
  try {
    const body = {
      username,
      comentario,
      centro_costo: centroCosto,
      estado,
    };

    const res = await getApi().post(`/factura/estado/${id}`, body);
    return res.data;
  } catch (error) {
    sentry.captureException(error);
    throw error;
  }
};

export const descargarFactura = async (id) => {
  try {
    const res = await getApi().get(`/factura/descargar/${id}`, { responseType: 'blob' });
    const contentType = res?.headers?.['content-type'] || 'application/pdf';
    const cd = res?.headers?.['content-disposition'] || '';
    let filename = `factura_${id}.pdf`;
    const match = cd.match(/filename\*=UTF-8''([^;\n\r]+)/) || cd.match(/filename="?([^";\n\r]+)"?/);
    if (match && match[1]) {
      filename = decodeURIComponent(match[1]).trim();
    }
    const blob = new Blob([res.data], { type: contentType });
    return { blob, filename };
  } catch (error) {
    sentry.captureException(error);
    throw error;
  }
};


export const getUsuarioPorUsername = async (username) => {
  try {
    const res = await getApi().get(`/usuario/${username}`);
    return res.data;
  } catch (error) {
    sentry.captureException(error);
    throw error;
  }
};

export const getFacturaHistorico = async (id) => {
  try {
    const res = await getApi().get("/factura/historico", {
      params: { id }
    });
    return res.data;
  } catch (error) {
    sentry.captureException(error);
    throw error;
  }
};

/**
 * Obtiene sectores de destino
 * @param {string} username - Username del usuario
 * @param {number} centroCosto - Centro de costo del usuario
 */
export const getSectorDestino = async (username = null, centroCosto = null) => {
  try {
    const res = await getApi().get("/dropdown/sector-destino", {
      params: {
        username,
        centroCosto
      }
    });
    return res.data;
  } catch (error) {
    sentry.captureException(error);
    throw error;
  }
};

/**
 * Obtiene listado de archivos asociados a una factura
 * @param {number} id - ID del comprobante
 */
export const getArchivosFactura = async (id) => {
  try {
    const res = await getApi().get(`/factura/archivos/${id}`);
    return res.data;
  } catch (error) {
    sentry.captureException(error);
    throw error;
  }
};

/**
 * Descarga un archivo específico de una factura
 * @param {Object} params - Parámetros de descarga
 * @param {number} params.idarchivo - ID del archivo en el repositorio (opcional)
 * @param {string} params.path - Ruta del archivo en el sistema de archivos (opcional)
 */
export const descargarArchivoFactura = async ({ idarchivo, path }) => {
  try {
    console.log('descargarArchivoFactura - Parámetros recibidos:', { idarchivo, path });
    
    let blob;
    let filename = 'archivo_descargado';
    let contentType = 'application/octet-stream';

    // Caso 1: Con idarchivo - el endpoint devuelve el archivo directamente como blob
    if (idarchivo) {
      console.log('Descargando con idarchivo:', idarchivo);
      
      const res = await getApi().get("/factura/descarga-archivo", {
        params: { idarchivo },
        responseType: 'blob'
      });

      console.log('Respuesta recibida - headers:', res.headers);
      console.log('Respuesta recibida - data type:', typeof res.data);

      contentType = res?.headers?.['content-type'] || 'application/octet-stream';
      const cd = res?.headers?.['content-disposition'] || '';

      // Intentar extraer el nombre del archivo desde content-disposition
      const match = cd.match(/filename\*=UTF-8''([^;\n\r]+)/) || cd.match(/filename="?([^";\n\r]+)"?/);
      if (match && match[1]) {
        filename = decodeURIComponent(match[1]).trim();
      }

      console.log('Archivo descargado:', { filename, contentType });
      blob = new Blob([res.data], { type: contentType });
    } 
    // Caso 2: Con path - el endpoint devuelve JSON con base64
    else if (path) {
      console.info('Descargando con path:', path);
      
      const res = await getApi().get("/factura/descarga-archivo", {
        params: { path }
      });

      console.info('Respuesta JSON recibida:', { hasBase64: !!res.data?.base64, mimeType: res.data?.mimeType, name: res.data?.name });

      const { base64, mimeType, name } = res.data;
      
      // Determinar el tipo de contenido
      contentType = mimeType || 'application/octet-stream';
      filename = name || 'documento';
      
      // Convertir base64 a blob
      const byteCharacters = atob(base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      blob = new Blob([byteArray], { type: contentType });
      
      // Si no viene nombre en la respuesta, extraer del path
      if (!name) {
        const pathParts = path.split('\\');
        filename = pathParts[pathParts.length - 1];
      }
      
      console.info('Archivo convertido desde base64:', { filename, contentType });
    } else {
      throw new Error('Se requiere idarchivo o path para descargar el archivo');
    }

    console.info('Retornando blob:', { blobSize: blob.size, filename });
    return { blob, filename };
  } catch (error) {
    console.error('Error en descargarArchivoFactura:', error);
    sentry.captureException(error);
    throw error;
  }
};
